/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Form, DateInput, Button, Tooltip } from '@mairistem/composants';
import { Section } from '@mairistem/administres-utils';

import { ModalRegistreTreeView } from '@/components/Common';
import {
  DATE_FORMAT,
  TYPE_REGISTRE_BY_TYPE_ACTE,
  URL_SOUS_TYPES_ACTE_NAISSANCE,
} from '@/constants';
import { AssistantContext } from '@/contexts';
import { useOptionsReferentiel, useRegistreParDefaut } from '@/hooks';
import { createTimeInputHandler } from '@/utils';

interface InformationsActeProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (
    event: React.SyntheticEvent | null,
    payload: { name: string; value: unknown },
  ) => void;
  /**
   * URL du référentiel de sous-type d'acte (défaut : sous-types de
   * naissance, comportement historique).
   */
  urlSousTypesActe?: string;
  /**
   * Types de registre proposés à la sélection, et interrogés pour le
   * préremplissage (défaut : registres de naissance).
   *
   * ⚠️ Ce défaut était documenté mais pas appliqué : `StepActeNaissance` ne
   * passe pas la prop, si bien que la valeur arrivait `undefined`. La modale y
   * survivait grâce à son propre repli interne, mais le préremplissage, lui,
   * s'abstenait faute de type à interroger.
   */
  typeRegistre?: string;
  /** Libellé du dropdown de sous-type (défaut : « Sous type d'acte »). */
  labelSousTypeActe?: string;
  /**
   * Sous-bloc rendu en fin de bloc, dans le même `Form`.
   *
   * Sert à l'officier d'état civil du mariage, que la maquette range sous les
   * informations de l'acte plutôt qu'à côté. Absent des autres types d'acte, où
   * le bloc reste inchangé.
   */
  children?: React.ReactNode;
}

const InformationsActe = (props: InformationsActeProps) => {
  const {
    data,
    errors = {},
    onChange,
    urlSousTypesActe = URL_SOUS_TYPES_ACTE_NAISSANCE,
    typeRegistre = TYPE_REGISTRE_BY_TYPE_ACTE.naissance,
    labelSousTypeActe = "Sous type d'acte",
    children,
  } = props;

  const { selectedRegistre, setSelectedRegistre } =
    React.useContext(AssistantContext);

  const [openModal, setOpenModal] = React.useState(false);

  /**
   * Présélection du registre : celui de l'année en cours s'il existe, sinon le
   * plus récent. Ne se déclenche que si aucun registre n'est choisi — donc à
   * l'arrivée sur l'assistant et après une remise à zéro, jamais par-dessus la
   * sélection de l'agent.
   */
  const registreParDefaut = useRegistreParDefaut(
    typeRegistre,
    _.isNil(selectedRegistre),
  );

  React.useEffect(() => {
    if (!_.isNil(registreParDefaut) && _.isNil(selectedRegistre)) {
      setSelectedRegistre(registreParDefaut);
    }
  }, [registreParDefaut, selectedRegistre, setSelectedRegistre]);

  /**
   * Options du dropdown « Sous type d'acte » (identifiant en value, libellé en
   * texte) + liste brute des items, nécessaire pour dériver le code métier
   * (`tr_codj`/`ta_codj`) attendu par les validations front.
   */
  const { options: sousTypeActeOptions, items: sousTypesActe } =
    useOptionsReferentiel(urlSousTypesActe);

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      onChange(null, {
        name: 'dateActe',
        value: _.isNil(date) ? null : dayjs(date).format(DATE_FORMAT),
      });
    },
    [onChange],
  );

  const handleNumeroInput = React.useCallback(
    (
      e: React.SyntheticEvent | null,
      { name, value }: { name: string; value: string },
    ) => {
      onChange(e, { name, value: value.replace(/[^0-9]/g, '') });
    },
    [onChange],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(onChange),
    [onChange],
  );

  /**
   * Le back attend l'`identifiant` (ex. `tr_auto`) dans `sousTypeActe`, mais
   * les règles de validation front (ex. reconnaissance prénatale) portent sur
   * le CODE métier (`tr_codj`/`ta_codj`, ex. `'N'`/`'V'`/`'D'`). On stocke donc
   * les deux valeurs, dérivées en même temps que le changement du dropdown.
   * Les deux `onChange` sont sûrs à enchaîner grâce au `setData(prev => …)` du
   * provider de contexte.
   */
  const handleSousTypeChange = React.useCallback(
    (
      e: React.SyntheticEvent | null,
      { name, value }: { name: string; value: unknown },
    ) => {
      onChange(e, { name, value });
      const item = _.find(sousTypesActe, { identifiant: value });
      onChange(null, {
        name: 'codeSousTypeActe',
        value: item?.codeEtatCivil ?? item?.codeJustice ?? null,
      });
    },
    [onChange, sousTypesActe],
  );

  return (
    <Section title="INFORMATIONS DE L'ACTE" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <Form.Group>
          <Tooltip
            trigger={
              <Form.Input
                width={10}
                required
                name="registre"
                label="Acte enregistré sur le registre"
                placeholder="Sélectionner un registre"
                value={
                  selectedRegistre
                    ? `Année ${selectedRegistre.annee?.annee} - ${selectedRegistre.libelle}`
                    : ''
                }
                error={!_.isNil(errors?.registre)}
                action={
                  <Button
                    type="button"
                    basic
                    data-testid="prenoms-input-bouton-plus"
                    icon="search"
                    onClick={() => setOpenModal(true)}
                  />
                }
                onChange={onChange}
              />
            }
            type={errors?.registre ? 'error' : ''}
            message={errors?.registre}
          />
          <Form.Dropdown
            width={6}
            selection
            required
            name="sousTypeActe"
            label={labelSousTypeActe}
            placeholder={`${labelSousTypeActe}`}
            options={sousTypeActeOptions}
            value={data?.sousTypeActe ?? ''}
            error={!_.isNil(errors?.sousTypeActe)}
            onChange={handleSousTypeChange}
          />
        </Form.Group>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Form.Field required error={!_.isNil(errors?.dateActe)}>
            <label>dressé le</label>
            <Tooltip
              trigger={
                <DateInput
                  value={
                    data?.dateActe
                      ? dayjs(data.dateActe, DATE_FORMAT).toDate()
                      : undefined
                  }
                  onChange={handleDateChange}
                />
              }
              type={errors?.dateActe ? 'error' : ''}
              message={errors?.dateActe}
            />
          </Form.Field>
          <Form.Group style={{ alignItems: 'flex-end' }}>
            <Tooltip
              trigger={
                <Form.Input
                  required
                  label="à"
                  placeholder="hh"
                  name="heureActe"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.heureActe)}
                  value={
                    !_.isNil(data?.heureActe)
                      ? String(data.heureActe).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.heureActe ? 'error' : ''}
              message={errors?.heureActe}
            />
            <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>:</span>
            <Tooltip
              trigger={
                <Form.Input
                  placeholder="mm"
                  name="minuteActe"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.minuteActe)}
                  value={
                    !_.isNil(data?.minuteActe)
                      ? String(data.minuteActe).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.minuteActe ? 'error' : ''}
              message={errors?.minuteActe}
            />
          </Form.Group>
          <Tooltip
            trigger={
              <Form.Input
                required
                label="de numéro"
                name="numeroActe"
                style={{ width: 130 }}
                error={!_.isNil(errors?.numeroActe)}
                value={data?.numeroActe ?? ''}
                onChange={handleNumeroInput}
              />
            }
            type={errors?.numeroActe ? 'error' : ''}
            message={errors?.numeroActe}
          />
          <Tooltip
            trigger={
              <Form.Input
                label="d'ordre"
                name="ordreActe"
                style={{ width: 130 }}
                error={!_.isNil(errors?.ordreActe)}
                value={data?.ordreActe ?? ''}
                onChange={onChange}
              />
            }
            type={errors?.ordreActe ? 'error' : ''}
            message={errors?.ordreActe}
          />
          <Tooltip
            trigger={
              <Form.Input
                label="et de complément"
                name="complementActe"
                style={{ width: 130 }}
                error={!_.isNil(errors?.complementActe)}
                value={data?.complementActe ?? ''}
                onChange={onChange}
              />
            }
            type={errors?.complementActe ? 'error' : ''}
            message={errors?.complementActe}
          />
        </div>
        {children}
      </Form>
      <ModalRegistreTreeView
        open={openModal}
        typeRegistre={typeRegistre}
        onClose={() => setOpenModal(false)}
        onSelect={setSelectedRegistre}
      />
    </Section>
  );
};

export default InformationsActe;
